import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { errorMessage } from '@/lib/api'
import { useUpdatePlan } from '@/lib/queries'
import type { Plan } from '@/lib/types'

const LIMITS: { key: string; label: string }[] = [
  { key: 'companies', label: 'Companies' },
  { key: 'users', label: 'Members' },
  { key: 'integrations', label: 'Integrations' },
]
const ALL = '*'
const label = (f: string) => f.replace(/_/g, ' ')

function Check({ id, checked, onChange, children, disabled }: { id: string; checked: boolean; onChange: (v: boolean) => void; children: string; disabled?: boolean }) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm">
      <input id={id} type="checkbox" className="size-4 accent-[var(--primary)]" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {children}
    </label>
  )
}

/** Edits one plan. Plans can't be created or deleted, and the id, group and billing period are fixed. */
export function PlanEditDialog({ plan, allFeatures, unlimited, open, onOpenChange }: { plan: Plan; allFeatures: string[]; unlimited: number; open: boolean; onOpenChange: (open: boolean) => void }) {
  const save = useUpdatePlan()
  const free = plan.group === 'free'
  const [name, setName] = useState(plan.name)
  const [price, setPrice] = useState((plan.price_cents / 100).toString())
  const [everything, setEverything] = useState(plan.features.includes(ALL))
  const [features, setFeatures] = useState<string[]>(plan.features.filter((f) => f !== ALL))
  const [limits, setLimits] = useState<Record<string, string>>(Object.fromEntries(LIMITS.map((l) => [l.key, String(plan.limits[l.key] ?? 0)])))
  const [stripePrice, setStripePrice] = useState(plan.stripe_price_id)
  const [isPublic, setIsPublic] = useState(plan.public)
  const [sort, setSort] = useState(String(plan.sort))

  const toggle = (f: string, on: boolean) => setFeatures((cur) => (on ? [...cur, f] : cur.filter((x) => x !== f)))

  async function submit() {
    const cents = Math.round(Number(price) * 100)
    const lim = Object.fromEntries(LIMITS.map((l) => [l.key, Math.round(Number(limits[l.key]))]))
    if (!Number.isFinite(cents) || Object.values(lim).some((v) => !Number.isFinite(v))) {
      toast.error('Price and limits have to be numbers')
      return
    }
    try {
      await save.mutateAsync({
        id: plan.id,
        changes: {
          name,
          price_cents: cents,
          features: everything ? [ALL] : features,
          limits: lim,
          stripe_price_id: free ? undefined : stripePrice,
          public: isPublic,
          sort: Math.round(Number(sort)) || 0,
        },
      })
      toast.success(`${name} saved`)
      onOpenChange(false)
    } catch (err) {
      toast.error(errorMessage(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {plan.name}</DialogTitle>
          <DialogDescription>
            <span className="font-mono">{plan.id}</span> · {plan.billing_type}. The change applies on this server right away; other servers pick it up when they restart.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="plan-name">Name</Label>
            <Input id="plan-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          {!free && (
            <div className="space-y-1.5">
              <Label htmlFor="plan-stripe">Stripe price id</Label>
              <Input id="plan-stripe" className="font-mono" placeholder="price_..." value={stripePrice} onChange={(e) => setStripePrice(e.target.value)} />
              <p className="text-xs text-muted-foreground">What Stripe bills this plan under. Checkout needs it, and a subscription is matched to its plan by it.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="plan-price">Price (kr per {plan.billing_type === 'yearly' ? 'year' : 'month'})</Label>
              <Input id="plan-price" type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
              <p className="text-xs text-muted-foreground">For reports only. Stripe charges its own price.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan-sort">Order</Label>
              <Input id="plan-sort" type="number" step="1" value={sort} onChange={(e) => setSort(e.target.value)} />
            </div>
          </div>

          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium">Features</legend>
            <Check id="f-all" checked={everything} onChange={setEverything}>
              Everything, including features added later
            </Check>
            <div className="grid grid-cols-2 gap-2">
              {allFeatures.map((f) => (
                <Check key={f} id={`f-${f}`} checked={everything || features.includes(f)} disabled={everything} onChange={(on) => toggle(f, on)}>
                  {label(f)}
                </Check>
              ))}
            </div>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium">Limits</legend>
            <div className="grid grid-cols-3 gap-3">
              {LIMITS.map((l) => (
                <div key={l.key} className="space-y-1.5">
                  <Label htmlFor={`l-${l.key}`}>{l.label}</Label>
                  <Input id={`l-${l.key}`} type="number" min={0} step="1" value={limits[l.key]} onChange={(e) => setLimits((cur) => ({ ...cur, [l.key]: e.target.value }))} />
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{unlimited.toLocaleString('en-US')} means no cap. The panel flags teams close to a limit; the app does not enforce limits yet.</p>
          </fieldset>

          <Check id="plan-public" checked={isPublic} disabled={free} onChange={setIsPublic}>
            Offered to customers (shown in the app and sold at checkout)
          </Check>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={save.isPending || !name.trim()} onClick={submit}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
